import React, { useEffect, useState } from "react";
import axios from "axios";
import Button from "../components/Button";
import { API_BASE } from "../utils/apiConfig";
import "./Verify.css";

export default function VerificationPending({ email, emailSent = true, initialMessage = "", onAuth }) {
  const [resending, setResending] = useState(false);
  const [message, setMessage] = useState(initialMessage);
  const [messageTone, setMessageTone] = useState(emailSent ? "success" : "warning");
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = window.setInterval(() => {
      setCooldown((seconds) => Math.max(0, seconds - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [cooldown]);

  const resend = async () => {
    if (!email || resending || cooldown > 0) return;
    setMessage("");
    try {
      setResending(true);
      await axios.post(`${API_BASE}/auth/resend-verification`, { email });
      setMessage("A new verification email has been sent. Please also check your spam or junk folder.");
      setMessageTone("success");
      setCooldown(30);
    } catch (err) {
      setMessage(err.response?.data?.error || "We could not resend the email. Please try again shortly.");
      setMessageTone("warning");
    } finally {
      setResending(false);
    }
  };

  if (!email) {
    return (
      <div className="verify-page">
        <div className="verify-card">
          <div className="verify-icon" aria-hidden="true">!</div>
          <h2>Email address unavailable</h2>
          <p className="subtitle">Return to sign up and enter your email address again.</p>
          <Button className="verify-btn" onClick={() => onAuth("signup")}>Return to sign up</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="verify-page">
      <div className="verify-card pending-card">
        <div className="verify-icon" aria-hidden="true">✓</div>
        <p className="verify-eyebrow">Account created</p>
        <h2>Check your email</h2>
        <p className="subtitle">
          {emailSent
            ? <>We sent a verification link to <strong>{email}</strong>. Verify your email before signing in.</>
            : <>Your account was created for <strong>{email}</strong>, but the verification email was not accepted by the provider. Use resend below.</>}
        </p>

        <div className="verify-steps">
          <div><span>1</span><p>Open the verification email from LogicLine.</p></div>
          <div><span>2</span><p>Select <strong>Verify Email Address</strong>.</p></div>
          <div><span>3</span><p>Return here and sign in.</p></div>
        </div>

        {message && <div className={`verify-message ${messageTone}`} role="status">{message}</div>}

        <Button className="verify-btn" onClick={() => onAuth("login", { email })}>
          Continue to sign in
        </Button>
        <button className="verify-secondary" onClick={resend} disabled={resending || cooldown > 0}>
          {resending ? "Sending…" : cooldown > 0 ? `Resend available in ${cooldown}s` : "Resend verification email"}
        </button>

        <p className="verify-footer">
          Wrong email? <span onClick={() => onAuth("signup")}>Create an account with a different email</span>
        </p>
      </div>
    </div>
  );
}
